import { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';
import toast from '../utils/toast';
import socket from '../utils/socket';

const FriendContext = createContext();

export function useFriends() {
  return useContext(FriendContext);
}

export function FriendProvider({ children }) {
  const { currentUser } = useAuth();
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userStatus, setUserStatus] = useState('online');
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Force refresh trigger
  const statusUpdateInProgress = useRef(false);
  const hasInitialized = useRef(false);
  const fetchInProgress = useRef({ friends: false, requests: false }); // Track fetch state

  // Fetch friends and friend requests when user logs in
  useEffect(() => {
    if (currentUser && !hasInitialized.current) {
      hasInitialized.current = true;
      
      fetchFriends();
      fetchFriendRequests();
      updateUserStatus('online');
      
      // Join user room for real-time updates
      
      socket.emit('joinUserRoom', currentUser._id);
      
      // Remove any existing listeners first (prevent duplicates)
      socket.off('friend-request-received');
      socket.off('friend-request-sent');
      socket.off('friend-request-accepted');
      socket.off('friend-request-declined');
      socket.off('friend-removed');
      
      // Listen for real-time friend events
      socket.on('friend-request-received', (payload) => {

        // ⚡ INSTANT: Add the request optimistically to the UI
        const newRequest = {
          from: {
            _id: payload.fromUser._id,
            username: payload.fromUser.username,
            avatar: payload.fromUser.avatar
          },
          _id: payload.fromUser._id
        };
        
        setFriendRequests(prev => {
          // Check if already exists
          const exists = prev.some(r => (r.from?._id || r.from) === payload.fromUser._id);
          if (exists) {
            
            return prev;
          }
          
          return [newRequest, ...prev];
        });
        
        // Force re-render
        setRefreshTrigger(prev => prev + 1);
        
        // Show toast notification
        toast.info(`${payload.fromUser.username} sent you a friend request!`, { 
          position: 'top-right', 
          className: 'small-toast' 
        });
      });
      
      socket.on('friend-request-sent', (payload) => {
        
        // Sender's side - just update refresh trigger, no need to refetch
        setRefreshTrigger(prev => prev + 1);
      });
      
      socket.on('friend-request-accepted', (payload) => {
        
        // ⚡ Socket event received - data already updated via optimistic update
        // Just update the refresh trigger to force re-render
        setRefreshTrigger(prev => prev + 1);
        
      });
      
      socket.on('friend-request-declined', (payload) => {
        
        // ⚡ INSTANT: Remove from requests optimistically
        setFriendRequests(prev => prev.filter(r => (r.from?._id || r.from) !== payload.userId));
        // Update refresh trigger
        setRefreshTrigger(prev => prev + 1);
      });
      
      socket.on('friend-removed', (payload) => {
        
        // ⚡ INSTANT: Remove from friends optimistically
        setFriends(prev => prev.filter(f => f._id !== payload.userId));
        // Update refresh trigger
        setRefreshTrigger(prev => prev + 1);
      });
    } else if (!currentUser) {
      setFriends([]);
      setFriendRequests([]);
      setLoading(false);
      hasInitialized.current = false;
    }
    
    // Cleanup listeners on unmount
    return () => {
      if (currentUser) {
        updateUserStatus('offline');
        socket.off('friend-request-received');
        socket.off('friend-request-sent');
        socket.off('friend-request-accepted');
        socket.off('friend-request-declined');
        socket.off('friend-removed');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Fetch friends list - NO THROTTLING for instant updates
  const fetchFriends = async () => {
    // Prevent duplicate simultaneous fetches
    if (fetchInProgress.current.friends) {
      
      return friends;
    }
    
    fetchInProgress.current.friends = true;
    
    try {
      
      // Add timestamp to bypass any caching
      const timestamp = Date.now();
      const response = await api.get(`/api/users/friends?_t=${timestamp}`);
      const friendsData = response.data?.friends || response.data || [];
      
      setFriends(friendsData);
      setError('');
      return friendsData;
    } catch (err) {
      
      setError('Failed to fetch friends');
      return [];
    } finally {
      fetchInProgress.current.friends = false;
    }
  };

  // Fetch friend requests - NO THROTTLING for instant updates
  const fetchFriendRequests = async () => {
    // Prevent duplicate simultaneous fetches
    if (fetchInProgress.current.requests) {
      
      return friendRequests;
    }
    
    fetchInProgress.current.requests = true;
    
    try {
      
      // Add timestamp to bypass any caching
      const timestamp = Date.now();
      const response = await api.get(`/api/users/friend-requests?_t=${timestamp}`);
      const requestsData = response.data?.requests || response.data || [];
      
      setFriendRequests(requestsData);
      setError('');
      setLoading(false); // Ensure loading is set to false
      return requestsData;
    } catch (err) {
      
      setError('Failed to fetch friend requests');
      setLoading(false);
      return [];
    } finally {
      fetchInProgress.current.requests = false;
    }
  };

  // Search users by username or email
  const searchUsers = async (query) => {
    try {
      // Add timestamp to bypass any caching
      const timestamp = Date.now();
      const response = await api.get(`/api/users/search?term=${encodeURIComponent(query)}&_t=${timestamp}`);
      return response.data?.users || response.data || [];
    } catch (err) {
      
      setError('Failed to search users');
      toast.error('Failed to search users', { position: 'top-right', className: 'small-toast' });
      return [];
    }
  };

  const sendFriendRequest = async (userId) => {
    try {

      // ⚡ INSTANT: Optimistically update search results to show "Request Sent"
      // This will be reflected in the search results immediately
      
      // Send request to server
      await api.post(`/api/users/send-friend-request/${userId}`);
      toast.success('Friend request sent!', { position: 'top-right', className: 'small-toast' });
      
      // ✅ Update refresh trigger to force search results to re-render
      setRefreshTrigger(prev => prev + 1);
      
      return true;
    } catch (err) {
      
      const errorMessage = err.response?.data?.message || err.response?.data?.msg || 'Failed to send friend request';
      setError(errorMessage);
      toast.error(errorMessage, { position: 'top-right', className: 'small-toast' });
      return false;
    }
  };

  // Respond to friend request (accept/reject) with optimistic updates + immediate refetch
  const respondToFriendRequest = async (userId, response) => {
    if (!userId) {
      toast.error('Invalid user ID', { position: 'top-right', className: 'small-toast' });
      return false;
    }

    // Find the request to get user info
    const request = friendRequests.find(r => (r.from?._id || r.from) === userId);
    const userInfo = request?.from || { _id: userId, username: 'Friend', avatar: null };

    // ⚡ INSTANT UI UPDATE - No waiting!
    // Remove from requests immediately
    setFriendRequests(prev => {
      const updated = prev.filter(r => (r.from?._id || r.from) !== userId);
      
      return updated;
    });
    
    // If accepting, add to friends list immediately
    if (response === 'accept') {
      const newFriend = {
        _id: userInfo._id || userId,
        username: userInfo.username || 'Friend',
        avatar: userInfo.avatar || null,
        status: 'online'
      };
      setFriends(prev => {
        const updated = [newFriend, ...prev];
        
        return updated;
      });
      toast.success(`You're now friends with ${newFriend.username}!`, { position: 'top-right', className: 'small-toast' });
    } else {
      toast.info('Friend request declined', { position: 'top-right', className: 'small-toast' });
    }

    // 🚀 Save to MongoDB - NO REFETCH (socket event will handle it)
    try {
      
      await api.post(`/api/users/respond-friend-request/${userId}/${response}`);

      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.msg || err.response?.data?.message || 'Failed to respond to friend request';
      
      // Don't show error toast if request was already processed (404)
      if (err.response?.status === 404) {
        
        // Request was already processed - refetch to sync
        await Promise.all([fetchFriends(), fetchFriendRequests()]);
        return true;
      }
      
      // ❌ Revert optimistic update on real error
      
      toast.error(errorMessage, { position: 'top-right', className: 'small-toast' });
      
      // Revert the changes
      if (response === 'accept') {
        setFriends(prev => prev.filter(f => f._id !== userId));
      }
      if (request) {
        setFriendRequests(prev => [request, ...prev]);
      }
      
      return false;
    }
  };

  // Decline shortcut
  const declineFriendRequest = async (userId) => respondToFriendRequest(userId, 'reject');

  // Remove friend with optimistic updates + immediate refetch
  const removeFriend = async (userId) => {

    // Find the friend to get their info for potential revert
    const friend = friends.find(f => f._id === userId);
    
    // ⚡ INSTANT UI UPDATE - Remove from list immediately (NO WAITING!)
    setFriends(prev => {
      const updated = prev.filter(f => f._id !== userId);
      
      return updated;
    });
    toast.info(`${friend?.username || 'Friend'} removed`, { position: 'top-right', className: 'small-toast' });
    
    // 🚀 Delete from MongoDB and refetch immediately
    try {
      
      await api.post(`/api/users/remove-friend/${userId}`);

      // ✅ Immediately refetch fresh data from server (NO DELAY)
      
      const friendsData = await fetchFriends();

      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to remove friend';
      
      toast.error(errorMessage, { position: 'top-right', className: 'small-toast' });
      
      // Revert optimistic update
      if (friend) {
        
        setFriends(prev => [friend, ...prev]);
      }
      
      return false;
    }
  };

  // Update user status (online, offline, busy, away)
  const updateUserStatus = async (status) => {
    try {
      if (!currentUser || statusUpdateInProgress.current || userStatus === status) return false;
      
      statusUpdateInProgress.current = true;
      setUserStatus(status);
      await api.put('/api/users/status', { status });
      statusUpdateInProgress.current = false;
      return true;
    } catch (err) {
      statusUpdateInProgress.current = false;
      // Silently fail for network errors - don't spam console
      if (err.code !== 'ERR_NETWORK' && !err.message?.includes('CORS')) {
        
      }
      return false;
    }
  };

  const value = {
    friends,
    friendRequests,
    loading,
    error,
    userStatus,
    refreshTrigger, // Expose refresh trigger
    fetchFriends,
    fetchFriendRequests,
    searchUsers,
    sendFriendRequest,
    respondToFriendRequest,
    declineFriendRequest,
    removeFriend,
    updateUserStatus,
    setUserStatus
  };

  return (
    <FriendContext.Provider value={value}>
      {children}
    </FriendContext.Provider>
  );
}